
import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress } from "@mui/material";
import Select from "react-select";
import "./CodingKpisFilter.css";
import filesummary from "../../../../../assets/filesummary.png";

import {
  fetchColumnFilterOptions,
  batchUpdateAppliedFilters,
  resetCoidngKPISFilters,
  initialCodingKpisFilters,
} from "../../redux/ReportsSlice";

import { multiSelectStyles } from "../../multiSelectStyle";
import { getColumnFilterEndpoints } from "../../optionsConfig";
import { DatePickerWithValidation, formatYYYYMMDD, dateFieldConfig } from "../../../../../utils/DateValidator";


const REPORT_PATH = "/coding-kpis-report";
const SESSION_KEY = "codingKpis_localFilters";


// Episode type is frontend-only static list — no backend call
const EPISODE_TYPE_OPTIONS = [
  { label: "Manually Coded", value: "Manually Coded" },
  { label: "AI Validated", value: "AI Validated" },
];

export default function CodingKpisFilter({ onApply, isFilterPage}) {
  const dispatch = useDispatch();
  const debounceRef = useRef({});
  const [searchTexts, setSearchTexts] = useState({});
  const [local, setLocal] = useState({ ...initialCodingKpisFilters });
  const [dateError, setDateError] = useState("");
  const [selectedCoderOptions, setSelectedCoderOptions] = useState([]);
  const [showCoderModal, setShowCoderModal] = useState(false);
  const savedFilters = useSelector(
    (s) => s.reports.appliedFilters[REPORT_PATH] || {}
  );

  const columnOptions = useSelector(
    (s) => s.reports.columnFilterOptions[REPORT_PATH] || {}
  );

  const columnConfig = getColumnFilterEndpoints(REPORT_PATH);

  // Sync local state from redux on mount
  // useEffect(() => {
  //   setLocal({ ...savedFilters });
  // }, []);
    //  Restore local state — Redux first, then sessionStorage
  // useEffect(() => {
  //   if (savedFilters?.start_date) {
  //     // Redux has data — use it directly
  //     setLocal({ ...savedFilters });
  //   } else {
  //     // Redux empty (refresh) — try sessionStorage
  //     const saved = sessionStorage.getItem(SESSION_KEY);
  //     if (saved) {
  //       try {
  //         setLocal(JSON.parse(saved));
  //       } catch {
  //         setLocal({ ...initialCodingKpisFilters });
  //       }
  //     }
  //   }
  // }, []);
useEffect(() => {
    if (isFilterPage) {
      // User navigated to filter page — fresh start
      setLocal({ ...initialCodingKpisFilters });
      dispatch(resetCoidngKPISFilters());
      sessionStorage.removeItem(SESSION_KEY);
    } else {
      // Reload case — restore from session
      if (savedFilters?.start_date) {
        setLocal({ ...savedFilters });
      } else {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (saved) {
          try { setLocal(JSON.parse(saved)); } 
          catch { setLocal({ ...initialCodingKpisFilters }); }
        }
      }
    }
  }, []);

  /* ───────── Fetch helpers (same pattern as HAC) ───────── */
  const dispatchFetch = (fieldName, search = "", page = 1) => {
    const config = columnConfig[fieldName];
    if (!config) return;
    dispatch(
      fetchColumnFilterOptions({
        reportPath: REPORT_PATH,
        columnKey: fieldName,
        endpoint: config.endpoint,
        responseKey: config.responseKey,
        search,
        page,
        limit: 10,
      })
    );
  };

  const handleInputChange = (fieldName, inputValue, { action } = {}) => {
    if (action === "input-change") setSearchTexts("");
    setSearchTexts((prev) => ({ ...prev, [fieldName]: inputValue }));
    if (debounceRef.current[fieldName]) clearTimeout(debounceRef.current[fieldName]);
    debounceRef.current[fieldName] = setTimeout(() => {
      dispatchFetch(fieldName, inputValue, 1);
    }, 500);
  };

  const handleMenuOpen = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    if (!columnState) {
      dispatchFetch(fieldName, "", 1);
      return;
    }
    const hasMore = columnState?.hasMore !== false;
    const count = columnState?.data?.length || 0;
    if (count < 5 && hasMore) {
      dispatchFetch(fieldName, searchTexts[fieldName] || "", (columnState.page || 1) + 1);
    }
  };

  const handleScrollBottom = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    if (!columnState || columnState.loading || columnState.hasMore === false) return;
    dispatchFetch(fieldName, searchTexts[fieldName] || "", (columnState.page ?? 1) + 1);
  };

 const mapToSelectOptions = (arr, fieldName) =>
  Array.isArray(arr)
    ? arr.map((i) => {
        if (typeof i === "object") {
          if (fieldName === "coder" || (i.user_id !== undefined && i.user_name !== undefined)) {
            return { label: i.user_name, value: i.user_id };
          }
          return {
            label: i.label || i.name || i.user_name || i.value,
            value: i.id || i.user_id || i.value || i.name,
          };
        }
        return { label: i, value: i };
      })
    : [];

  const getOptionsWithLoader = (fieldName) => {
    const columnState = columnOptions?.[fieldName];
    const baseOptions = mapToSelectOptions(columnState?.data || [],fieldName);
    if (columnState?.loading) {
      return [
        ...baseOptions,
        {
          value: "loading",
          label: (
            <div style={{ display: "flex", justifyContent: "center", padding: 8 }}>
              <CircularProgress size={18} />
            </div>
          ),
          isDisabled: true,
        },
      ];
    }
    return baseOptions;
  };

  /* ───────── Apply / Reset ───────── */
  const handleApplyFilters = () => {
      if (!local.start_date || !local.end_date) {
    setDateError("Please select both Start Date and End Date to generate the report.");
    return;
  }
  setDateError("");
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(local));

    // Persist full local state to Redux
    dispatch(batchUpdateAppliedFilters({ reportPath: REPORT_PATH, filters: local }));

    // API payload: only coder & site sent to backend; all other fields are empty
    const apiPayload = {
      user_id: local.coder || [],
      site: local.site || [],
      episodes_coded: local.episodes_coded || [],
      codinging_days: [],
      coding_days_operator: "",
      total_episodes_coded: [],
      total_episodes_coded_operator: "",
      manually_coded: [],
      manually_coded_operator: "",
      ai_validation_coded: [],
      ai_validation_coded_operator: "",
      nwau: [],
      average_los: [],
      average_los_operator: "",
      start_date: local.start_date || null,
      end_date: local.end_date || null,
      sort_by: local.sort_by || "",
      sort_order: local.sort_order || "",
    };

    onApply?.(apiPayload);
  };

  const handleResetFilters = () => {
    dispatch(resetCoidngKPISFilters());
    setLocal({ ...initialCodingKpisFilters });
    onApply?.({
      user_id: [],
      site: [],
      episodes_coded: [],
      codinging_days: [],
      coding_days_operator: "",
      total_episodes_coded: [],
      total_episodes_coded_operator: "",
      manually_coded: [],
      manually_coded_operator: "",
      ai_validation_coded: [],
      ai_validation_coded_operator: "",
      nwau: [],
      average_los: [],
      average_los_operator: "",
      start_date: null,
      end_date: null,
      sort_by: "",
      sort_order: "",
    });
  };
  const handleClearFilters = () => {
    dispatch(resetCoidngKPISFilters());
    sessionStorage.removeItem(SESSION_KEY); 
    setLocal({ ...initialCodingKpisFilters }); // ← just clear all fields
     setSelectedCoderOptions([]); //  clear selected coders display
     setShowCoderModal(false);
    // no onApply, no navigation, nothing else
  };
  /* ───────── Footer summary ───────── */
  const summaryDateRange = () => {
    if (local.start_date && local.end_date) return `${local.start_date} - ${local.end_date}`;
    if (local.start_date) return `From ${local.start_date}`;
    if (local.end_date) return `To ${local.end_date}`;
    return "No Date Selected" ;
  };

  const summaryCoders = () => {
    const count = (local.coder || []).length;
    return count === 0 ? "All Coders Selected" : `${count} Coder${count > 1 ? "s" : ""} Selected`;
  };

  const summarySites = () => {
  const count = (local.site || []).length;
  return count === 0 ? "All Sites" : `${count} Site${count > 1 ? "s" : ""} Selected`;
};

  /* ───────── UI — original layout, no extra fields ───────── */
  return (
    <div className="kpi-page">
      <h3 className="kpi-title">Coding KPIS Report</h3>
      <p className="kpi-subtitle">
        Track coding performance across coders, sites, and date ranges
      </p>

      <div className="kpi-card">

        {/* CODER — backend */}
        {/* <label className="kpi-label-coder">Coder Selection</label>
        <Select
          isMulti
          className="filter-model-input-option-field"
          styles={multiSelectStyles}
          options={getOptionsWithLoader("coder")}
          value={getOptionsWithLoader("coder").filter((o) =>
            (local.coder || []).includes(o.value)
          )}
          closeMenuOnSelect={false}
          placeholder="Search coders..."
          isOptionDisabled={(opt) => opt.value === "loading"}
          onMenuOpen={() => handleMenuOpen("coder")}
          onMenuScrollToBottom={() => handleScrollBottom("coder")}
          onInputChange={(val, meta) => handleInputChange("coder", val, meta)}
          onChange={(selected) =>
            setLocal((prev) => ({ ...prev, coder: selected ? selected.map((o) => o.value) : [] }))
          }
        /> */}
        {/* CODER — backend */}
        <label className="kpi-label-coder">Coder Selection</label>
        <Select
          isMulti
          className="filter-model-input-option-field"
          styles={{
            ...multiSelectStyles,
            multiValue: () => ({ display: 'none' }),       //  hide default chips
            multiValueLabel: () => ({ display: 'none' }),
            multiValueRemove: () => ({ display: 'none' }),         
            clearIndicator: () => ({ display: 'none' }),
          }}
          options={getOptionsWithLoader("coder")}
          value={selectedCoderOptions}                     // full {label,value} objects
          closeMenuOnSelect={false}
          placeholder="Search coders..."
          isOptionDisabled={(opt) => opt.value === "loading"}
          onMenuOpen={() => handleMenuOpen("coder")}
          onMenuScrollToBottom={() => handleScrollBottom("coder")}
          onInputChange={(val, meta) => handleInputChange("coder", val, meta)}
          onChange={(selected) => {
            setSelectedCoderOptions(selected || []);
            setShowCoderModal(false)      // store full objects for display
            setLocal((prev) => ({
              ...prev,
              coder: selected ? selected.map((o) => o.value) : [], // only ids to API — same as before
            }));
          }}
        />
        {/*  Show first 3 tags + View All button */}
        {selectedCoderOptions.length > 0 && (
          <div className="coder-tags-container">

            {/* Always show only first 3 */}
            {selectedCoderOptions.slice(0, 3).map((opt) => (
              <span key={opt.value} className="coder-tag">
                {opt.label}
                <span
                  className="coder-tag-remove"
                  onClick={() => {
                    const updated = selectedCoderOptions.filter((o) => o.value !== opt.value);
                    setSelectedCoderOptions(updated);
                    setLocal((prev) => ({ ...prev, coder: updated.map((o) => o.value) }));
                  }}
                >×</span>
              </span>
            ))}

            {/* View All button — only if more than 3 */}
            {selectedCoderOptions.length > 3 && (
              <span className="coder-view-all" onClick={() => setShowCoderModal(true)}>
                View All ({selectedCoderOptions.length})
              </span>
            )}
          </div>
        )}

        {/*  Popup Modal */}
        {showCoderModal && (
          <div className="coder-modal-overlay" onClick={() => setShowCoderModal(false)}>
            <div className="coder-modal-box" onClick={(e) => e.stopPropagation()}>

              <div className="coder-modal-header">
                <span>Selected Coders ({selectedCoderOptions.length})</span>
                <span className="coder-modal-close" onClick={() => setShowCoderModal(false)}>×</span>
              </div>

              <div className="coder-modal-body">
                {selectedCoderOptions.map((opt) => (
                  <span key={opt.value} className="coder-tag">
                    {opt.label}
                    <span
                      className="coder-tag-remove"
                      onClick={() => {
                        const updated = selectedCoderOptions.filter((o) => o.value !== opt.value);
                        setSelectedCoderOptions(updated);
                        setLocal((prev) => ({ ...prev, coder: updated.map((o) => o.value) }));
                        if (updated.length === 0) setShowCoderModal(false);
                      }}
                    >×</span>
                  </span>
                ))}
              </div>

            </div>
          </div>
        )}
        {/* GRID: Episode Type + Site */}
        <div className="kpi-grid">

          {/* Episode Type — frontend only, no API call */}
          <div>
            <label className="kpi-label">Episode Type</label>
            <Select
              isMulti
              className="filter-model-input-option-field"
              styles={multiSelectStyles}
              options={EPISODE_TYPE_OPTIONS}
              value={EPISODE_TYPE_OPTIONS.filter((o) =>
                (local.episodes_coded || []).includes(o.value)
              )}
              closeMenuOnSelect={false}
              placeholder="Select episode type"
              onChange={(selected) =>
                setLocal((prev) => ({
                  ...prev,
                  episodes_coded: selected ? selected.map((o) => o.value) : [],
                }))
              }
            />
          </div>

          {/* Site — backend */}
          <div>
            <label className="kpi-label">Site Selection</label>
            <Select
              isMulti
              className="filter-model-input-option-field"
              styles={multiSelectStyles}
              options={getOptionsWithLoader("site")}
              value={getOptionsWithLoader("site").filter((o) =>
                (local.site || []).includes(o.value)
              )}
              closeMenuOnSelect={false}
              placeholder="Select site"
              isOptionDisabled={(opt) => opt.value === "loading"}
              onMenuOpen={() => handleMenuOpen("site")}
              onMenuScrollToBottom={() => handleScrollBottom("site")}
              onInputChange={(val, meta) => handleInputChange("site", val, meta)}
              onChange={(selected) =>
                setLocal((prev) => ({ ...prev, site: selected ? selected.map((o) => o.value) : [] }))
              }
            />
          </div>
        </div>

        {/* DATE RANGE */}
        {/* <label className="kpi-label mt">Date Range Selection</label> */}
        <label className="kpi-label mt">
          Date Range Selection <span style={{ color: "red" }}>*</span>
        </label>

        {dateError && (
          <div style={{ color: "red", fontSize: "13px", marginBottom: "8px" }}>
            {dateError}
          </div>
        )}
        <div className="date-container">
          <div className="kpi-tabs">
            {[
              { label: "Today", days: 0 },
              { label: "Last 7 days", days: 7 },
              { label: "Last 30 days", days: 30 },
            ].map(({ label, days }) => {
              const toDate = new Date();
              const fromDate = new Date();
              fromDate.setDate(toDate.getDate() - days);
              const fmt = (d) => d.toISOString().split("T")[0];
              const isActive =
                local.start_date === fmt(fromDate) && local.end_date === fmt(toDate);
              return (
                <span
                  key={label}
                  className={`date-tabs${isActive ? " active" : ""}`}
                  onClick={() =>{
                    setDateError("");
                    setLocal((prev) => ({
                      ...prev,
                      start_date: fmt(fromDate),
                      end_date: fmt(toDate),
                    }))}
                  }
                >
                  {label}
                </span>
              );
            })}
          </div>

          <div className="kpi-grid">
            <div className="kpi-date-field">
              <label className="kpi-label-date">START DATE</label>
              {/* <input
                type="date"
                className="kpi-date"
                value={local.start_date || ""}
                onChange={(e) =>{
                  setDateError("");
                  setLocal((prev) => ({ ...prev, start_date: e.target.value || null }))
                }}
              /> */}
              {DatePickerWithValidation(
                { name: 'start_date', label: 'Start Date', type: 'date' },
                local,
                (updater) => {
                  setDateError("");
                  setLocal(updater);
                }
              )}
            </div>
            <div className="kpi-date-field">
              <label className="kpi-label-date">END DATE</label>
              {/* <input
                type="date"
                className="kpi-date"
                value={local.end_date || ""}
                onChange={(e) =>{
                  setDateError("");
                  setLocal((prev) => ({ ...prev, end_date: e.target.value || null }))
                }}
              /> */}
              {DatePickerWithValidation(
                { name: 'end_date', label: 'End Date', type: 'date' },
                local,
                (updater) => {
                  setDateError("");
                  setLocal(updater);
                }
              )}
            </div>
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="kpi-footer">
        <div className="kpi-summary">
          <span className="file-image kpi-summary-item">
            <img src={filesummary} alt="file summary" className="kpi-icon-img kpi-summary-item" />
          </span>
          <span className="kpi-summary-item filesummary">Filter Summary</span>
          <span className="kpi-dot">•</span>
          <span className="kpi-summary-item">Date Range : {summaryDateRange()}</span>
          <span className="kpi-dot">•</span>
          <span className="kpi-summary-item">{summaryCoders()}</span>
          <span className="kpi-dot">•</span>
          <span className="kpi-summary-item">{summarySites()}</span>
        </div>

        <div className="kpi-actions">
          <button className="kpi-reset" onClick={handleClearFilters}>Reset Filters</button>
          <button className="kpi-generate" onClick={handleApplyFilters}>Generate Report</button>
        </div>
      </div>
    </div>
  );
}